import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type UploadFlowStationType =
    | "pencil-sketch"
    | "caricature"
    | "wynwood"
    | "famous-painting"
    | "cartoon"
    | "hobby-caricature"
    | null;

/** One chosen caricature hobby per captured person (order matches hobbyPersonImages). */
export interface HobbyPersonHobbySelection {
    id: string;
    name: string;
    category_id: string;
    icon_url: string;
}

interface UploadFlowState {
    uuid: string | null;
    type: UploadFlowStationType;
    qrImageUrl: string | null;
    uploadedImageUrl: string | null;
    processedImageUrl: string | null;
    /** Previous AI result kept when user taps "Generate one more" for side-by-side compare. */
    previousProcessedImageUrl: string | null;
    finalCanvasImages: { [key: string]: string };
    hobbyPeopleCount: number | null;
    hobbyPersonImages: string[];
    hobbyPersonHobbySelections: HobbyPersonHobbySelection[];
    /** Final chosen caricature URL per person (order matches hobbyPersonImages). */
    hobbyPersonCompletedResults: string[];
    /** True when people were extracted from a single group photo (skip per-person capture). */
    hobbyGroupPhotoFlow: boolean;
    isLoading: boolean;
    error: string | null;
}

const initialState: UploadFlowState = {
    uuid: null,
    type: null,
    qrImageUrl: null,
    uploadedImageUrl: null,
    processedImageUrl: null,
    previousProcessedImageUrl: null,
    finalCanvasImages: {},
    hobbyPeopleCount: null,
    hobbyPersonImages: [],
    hobbyPersonHobbySelections: [],
    hobbyPersonCompletedResults: [],
    hobbyGroupPhotoFlow: false,
    isLoading: false,
    error: null,
};

const uploadFlowSlice = createSlice({
    name: "uploadFlow",
    initialState,
    reducers: {
        setUuid(state, action: PayloadAction<string>) {
            state.uuid = action.payload;
        },
        setType(state, action: PayloadAction<UploadFlowStationType>) {
            state.type = action.payload;
        },
        setQrImageUrl(state, action: PayloadAction<string | null>) {
            state.qrImageUrl = action.payload;
        },
        setUploadedImageUrl(state, action: PayloadAction<string | null>) {
            state.uploadedImageUrl = action.payload;
        },
        setProcessedImageUrl(state, action: PayloadAction<string | null>) {
            state.processedImageUrl = action.payload;
        },
        setPreviousProcessedImageUrl(state, action: PayloadAction<string | null>) {
            state.previousProcessedImageUrl = action.payload;
        },
        clearPreviousProcessedImageUrl(state) {
            state.previousProcessedImageUrl = null;
        },
        setFinalCanvasImage(
            state,
            action: PayloadAction<{ key: string; image: string }>
        ) {
            const { key, image } = action.payload;
            if (!state.finalCanvasImages) state.finalCanvasImages = {};
            state.finalCanvasImages[key] = image;
        },
        initHobbyPeopleSession(state, action: PayloadAction<number>) {
            state.hobbyPeopleCount = action.payload;
            state.hobbyPersonImages = [];
            state.hobbyPersonHobbySelections = [];
            state.hobbyPersonCompletedResults = [];
            state.hobbyGroupPhotoFlow = false;
        },
        setHobbyGroupPhotoFlow(state, action: PayloadAction<boolean>) {
            state.hobbyGroupPhotoFlow = action.payload;
        },
        appendHobbyPersonImage(state, action: PayloadAction<string>) {
            if (!state.hobbyPersonImages) state.hobbyPersonImages = [];
            state.hobbyPersonImages.push(action.payload);
        },
        setHobbyPersonImages(state, action: PayloadAction<string[]>) {
            state.hobbyPersonImages = Array.isArray(action.payload) ? action.payload : [];
        },
        setHobbyPersonImageAt(
            state,
            action: PayloadAction<{ index: number; imageUrl: string }>
        ) {
            if (!state.hobbyPersonImages) state.hobbyPersonImages = [];
            const { index, imageUrl } = action.payload;
            while (state.hobbyPersonImages.length <= index) {
                state.hobbyPersonImages.push("");
            }
            state.hobbyPersonImages[index] = imageUrl;
        },
        appendHobbyPersonHobbySelection(
            state,
            action: PayloadAction<HobbyPersonHobbySelection>
        ) {
            if (!state.hobbyPersonHobbySelections) state.hobbyPersonHobbySelections = [];
            state.hobbyPersonHobbySelections.push(action.payload);
        },
        setHobbyPersonHobbySelectionAt(
            state,
            action: PayloadAction<{ index: number; selection: HobbyPersonHobbySelection }>
        ) {
            if (!state.hobbyPersonHobbySelections) state.hobbyPersonHobbySelections = [];
            state.hobbyPersonHobbySelections[action.payload.index] = action.payload.selection;
        },
        appendHobbyPersonCompletedResult(state, action: PayloadAction<string>) {
            if (!state.hobbyPersonCompletedResults) state.hobbyPersonCompletedResults = [];
            state.hobbyPersonCompletedResults.push(action.payload);
        },
        setHobbyPersonCompletedResults(state, action: PayloadAction<string[]>) {
            state.hobbyPersonCompletedResults = Array.isArray(action.payload)
                ? action.payload
                : [];
        },
        clearHobbyPersonHobbySelections(state) {
            state.hobbyPersonHobbySelections = [];
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
        resetFlow() {
            return initialState;
        },
    },
});

export const {
    setUuid,
    setType,
    setQrImageUrl,
    setUploadedImageUrl,
    setProcessedImageUrl,
    setPreviousProcessedImageUrl,
    clearPreviousProcessedImageUrl,
    setFinalCanvasImage,
    initHobbyPeopleSession,
    setHobbyGroupPhotoFlow,
    appendHobbyPersonImage,
    setHobbyPersonImages,
    setHobbyPersonImageAt,
    appendHobbyPersonHobbySelection,
    setHobbyPersonHobbySelectionAt,
    appendHobbyPersonCompletedResult,
    setHobbyPersonCompletedResults,
    clearHobbyPersonHobbySelections,
    setLoading,
    setError,
    resetFlow,
} = uploadFlowSlice.actions;

export default uploadFlowSlice.reducer;
